import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { getDisasterSummary } from "../../../../api";
import type { DisasterSummaryRow } from "../../../../types/disaster";

type SummaryColumnId =
  | "name"
  | "count_all_ss"
  | "count_confirm_ss"
  | "count_sale_ss"
  | "count_close_ss"
  | "count_damage_ss";

type SummaryColumn = {
  id: SummaryColumnId;
  label: string;
  minWidth: string;
  isSmallScreen: boolean;
};

const columns: SummaryColumn[] = [
  { id: "name", label: "集約区分", minWidth: "120px", isSmallScreen: true },
  {
    id: "count_all_ss",
    label: "SS数（報告対象数）",
    minWidth: "120px",
    isSmallScreen: true,
  },
  {
    id: "count_confirm_ss",
    label: "報告済件数",
    minWidth: "120px",
    isSmallScreen: false,
  },
  {
    id: "count_sale_ss",
    label: "営業件数",
    minWidth: "120px",
    isSmallScreen: false,
  },
  {
    id: "count_close_ss",
    label: "休業件数",
    minWidth: "120px",
    isSmallScreen: false,
  },
  {
    id: "count_damage_ss",
    label: "被害あり件数",
    minWidth: "120px",
    isSmallScreen: false,
  },
];

export default function PrefectureTable() {
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [selectedRow, setSelectedRow] = useState<DisasterSummaryRow | null>(
    null
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [summaries, setSummaries] = useState<DisasterSummaryRow[]>([]);

  const getDisasterSummaryData = async () => {
    if (!disasterId) {
      return;
    }
    try {
      const response = await getDisasterSummary(disasterId);
      if (response.status !== "ok") {
        return;
      }
      setSummaries(response.summary_by_pref ?? []);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    getDisasterSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    borderBottom: 2,
                    padding: "8px",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries.map((summary, index) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={String(summary.key ?? index)}
                onClick={() => {
                  setSelectedRow(summary);
                  setIsPopoverOpen(true);
                }}
                sx={{
                  pointerEvents: "none",
                  "@media screen and (max-width:900px)": {
                    cursor: "pointer",
                    pointerEvents: "auto",
                  },
                }}
                name={`prefectureData_${index}`}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      padding: "8px",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true
                            ? "table-cell"
                            : "none",
                      },
                    }}
                  >
                    {summary[column.id] as string | number | undefined}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={isPopoverOpen} onClose={() => setIsPopoverOpen(false)}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          }}
        >
          <Box sx={{ paddingLeft: "8px", fontWeight: "bold", fontSize: "20px" }}>
            都道府県別集計詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setIsPopoverOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {selectedRow &&
              columns.map((column) => (
                <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                  <TableCell
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                    }}
                  >
                    {column.label}
                  </TableCell>
                  <TableCell
                    sx={{
                      writingMode: "horizontal-tb",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                      overflowWrap: "break-word",
                    }}
                  >
                    {selectedRow[column.id] as string | number | undefined}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Dialog>
    </Paper>
  );
}
